<template>
  <div class="exp-list">
    <div v-for="node in items" :key="node.id" class="exp-node">
      <!-- FILA -->
      <div
        class="exp-row"
        :class="{ 'exp-row--group': hasChildren(node), 'exp-row--selected': node.id === selectedId }"
        :style="{ paddingLeft: (10 + level * 14) + 'px' }"
        @click="onRowClick(node)"
      >
        <!-- Chevron (solo si tiene hijos) o spacer para alinear -->
        <span class="exp-chevron" :class="{ 'exp-chevron--open': isExpanded(node) }">
          <svg v-if="hasChildren(node)" xmlns="http://www.w3.org/2000/svg" width="13" height="13"
               viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="m9 18 6-6-6-6"></path>
          </svg>
        </span>

        <!-- Contenido de la fila (slot #row para personalizar, o default del diseño) -->
        <div class="exp-body">
          <slot name="row" :node="node" :level="level" :expanded="isExpanded(node)">
            <div class="exp-head">
              <span class="exp-code" :style="{ color: colorOf(node) }">{{ node.code }}</span>
              <span v-if="node.subtitle" class="exp-sub">{{ node.subtitle }}</span>
              <span
                v-if="hasChildren(node) && !isExpanded(node) && descendantSelected(node)"
                class="exp-dot"
                :title="descendantTitle(node)"
                :style="{ background: colorOf(node) }"
              ></span>
              <span v-if="node.badge" class="exp-badge" :title="node.badge + ' ' + $t('linked_measurements').toLowerCase()">{{ node.badge }}</span>
              <span v-if="node.noLink" class="exp-nolink" :title="$t('no_measurement_link')">
                <b-icon pack="fas" icon="link-slash" size="is-small"></b-icon>
              </span>
            </div>
            <div class="exp-title" :title="node.title">{{ node.title }}</div>
            <div v-if="node.meta != null || node.amount != null" class="exp-meta">
              <span class="exp-meta__left">{{ node.meta }}</span>
              <span class="exp-meta__right">{{ node.amount }}</span>
            </div>
          </slot>
        </div>
      </div>

      <!-- HIJOS (recursivo) -->
      <expandable-list
        v-if="hasChildren(node) && isExpanded(node)"
        :items="node.children"
        :level="level + 1"
        :default-color="defaultColor"
        :value="value"
        :selected-id="selectedId"
        @select="$emit('select', $event)"
      >
        <template #row="slotProps">
          <slot name="row" v-bind="slotProps" />
        </template>
      </expandable-list>
    </div>
  </div>
</template>

<script>
/**
 * ExpandableList — lista expandible genérica y recursiva (basada en el diseño de
 * "Mediciones económicas del presupuesto"). Presentacional: recibe `items` y emite
 * `select`. Cada nodo:
 *   { id, code, title, meta, amount, color?, dot?, dotTitle?, children? }
 *
 * Slots:
 *   #row="{ node, level, expanded }" → personaliza el contenido de la fila.
 *
 * Estilos por <style scoped> (colores/tamaños del diseño) porque el Tailwind del
 * proyecto es viejo y no soporta valores arbitrarios (text-[10px], bg-[#hex], hover:).
 */
export default {
  name: 'expandable-list',

  props: {
    items: { type: Array, default: () => [] },
    // Nivel de anidamiento (uso interno para la sangría recursiva).
    level: { type: Number, default: 0 },
    // Color de acento por defecto (código + punto).
    defaultColor: { type: String, default: 'rgb(58, 107, 200)' },
    // (opcional) ids expandidos controlados desde el padre; si no, estado interno.
    value: { type: Array, default: null },
    // id del nodo seleccionado (resalta la fila; el grupo ancestro muestra un punto).
    selectedId: { type: [String, Number], default: null },
  },

  data() {
    return {
      // Estado interno de expansión cuando no se controla por `value`.
      openIds: [],
    }
  },

  methods: {
    hasChildren(node) {
      return Array.isArray(node.children) && node.children.length > 0
    },
    isExpanded(node) {
      const list = this.value || this.openIds
      return list.includes(node.id)
    },
    toggle(node) {
      if (this.value) {
        // Modo controlado: avisar al padre.
        const next = this.value.includes(node.id)
          ? this.value.filter((id) => id !== node.id)
          : [...this.value, node.id]
        this.$emit('input', next)
        return
      }
      this.openIds = this.openIds.includes(node.id)
        ? this.openIds.filter((id) => id !== node.id)
        : [...this.openIds, node.id]
    },
    onRowClick(node) {
      if (this.hasChildren(node)) {
        this.toggle(node)
      }
      this.$emit('select', node)
    },
    colorOf(node) {
      return node.color || this.defaultColor
    },
    // ¿algún descendiente (o el propio nodo) es el seleccionado? → punto en el grupo.
    descendantSelected(node) {
      if (this.selectedId == null) return false
      if (node.id === this.selectedId) return true
      return (node.children || []).some((c) => this.descendantSelected(c))
    },
    // Título del descendiente seleccionado (tooltip del punto).
    descendantTitle(node) {
      if (node.id === this.selectedId) return node.title || ''
      for (const c of node.children || []) {
        const t = this.descendantTitle(c)
        if (t) return t
      }
      return ''
    },
  },
}
</script>

<style scoped>
.exp-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 10px;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  background: #fff;            /* hojas (items) en blanco */
  transition: background-color 120ms ease;
}
/* Grupos (BOQ / con hijos): fondo gris. */
.exp-row--group {
  background: rgb(247, 249, 252);
}
.exp-row:hover {
  background: #f0f5fd;
}
/* Fila seleccionada: fondo azul claro + borde izquierdo de acento. */
.exp-row--selected {
  background: #eaf1fb !important;
  box-shadow: inset 3px 0 0 0 rgb(58, 107, 200);
}
.exp-chevron {
  flex-shrink: 0;
  margin-top: 2px;
  color: #9ca3af;
  display: inline-flex;
  width: 13px;
  transition: transform 120ms ease;
}
.exp-chevron--open {
  transform: rotate(90deg);
}
.exp-body {
  min-width: 0;
  flex: 1 1 auto;
}
.exp-head {
  display: flex;
  align-items: center;
  gap: 6px;
}
.exp-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  font-weight: 700;
}
.exp-sub {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 10px;
  font-weight: 600;
  color: #9ca3af;
}
.exp-dot {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  flex-shrink: 0;
}
/* Badge de nº de mediciones vinculadas (indica que ya hay asignación). */
.exp-badge {
  margin-left: auto;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 5px;
  border-radius: 9999px;
  font-size: 9.5px;
  font-weight: 700;
  color: #fff;
  background: rgb(39,174,96);
}
/* Indicador "sin mediciones": la línea BOQ no se vincula (se certifica aparte). */
.exp-nolink {
  margin-left: auto;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 9999px;
  color: rgb(87,83,78);
  background: #f0efee;
}
.exp-nolink + .exp-badge,
.exp-badge + .exp-nolink { margin-left: 4px; }
.exp-title {
  font-size: 12px;
  line-height: 1.3;
  font-weight: 600;
  color: rgb(30, 48, 70);
  /* Ellipsis a 3 líneas → unifica alto y evita textos larguísimos. */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.exp-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 2px;
  font-size: 10.5px;
  font-variant-numeric: tabular-nums;
}
.exp-meta__left {
  color: #9ca3af;
}
.exp-meta__right {
  font-weight: 600;
  color: #6b7280;
}
</style>
